import React, { useState } from "react";
import { C, font } from "../../../lib/styles.js";
import { bridge } from "../../../lib/bridgeCallables.js";

/**
 * Traffic Control → Transfers (SafeShare slice 5.2; docs/safeshare/design/
 * 02-traffic-control.md section 3.3). Read-only first: for one job (or one
 * capture's serial number), every file with its copies as columns, one
 * column per place (a studio computer's disk, the NAS, the cloud), and the
 * copy, verify and scan orders open or recently ended.
 *
 * One read, getFileCopies (admin only; a projection: no free text, no paths
 * outside root names, no links). A tick means a verified copy that counts
 * now; anything else says what it is. Today's old records (uploads, guest
 * runs, hand copies, handheld folders) are shown as "not checked" until the
 * producers write copy rows themselves.
 */

const STATE_TEXT = {
  verified: "✓",
  unverified: "not checked",
  suspect: "suspect",
  creating: "writing",
  deleting: "purging",
  deleted: "deleted",
  gone: "gone",
};

const WHY_TEXT = {
  root_not_confirmed: "root not confirmed",
  root_confirmed_since_check: "re-confirmed: check again",
  root_bound_elsewhere: "root bound elsewhere",
  not_hashed: "no hash",
  no_verifier: "no check",
};

/** The text for one copy. */
const oneText = (c) => {
  if (c.state === "verified") return c.counts ? "✓" : `✓ (${WHY_TEXT[c.why] || c.why || "does not count"})`;
  return STATE_TEXT[c.state] || c.state;
};

/** Best first: what a cell shows when one place holds the file at several paths. */
const RANK = (c) => (c.state === "verified" ? (c.counts ? 0 : 1) : {suspect: 2, creating: 3, unverified: 4, deleting: 5, gone: 6, deleted: 7}[c.state] ?? 8);

/**
 * The cell text for a place: one copy, or several (the same file at more
 * than one path in one place). The best is shown, with how many there are,
 * and a suspect one is never hidden.
 */
export const cellText = (cell) => {
  const list = Array.isArray(cell) ? cell : cell ? [cell] : [];
  if (!list.length) return "";
  const sorted = [...list].sort((a, b) => RANK(a) - RANK(b));
  let text = oneText(sorted[0]);
  if (list.length > 1) text += ` ×${list.length}`;
  const suspects = list.filter((c) => c.state === "suspect").length;
  if (suspects && sorted[0].state !== "suspect") text += ` · ${suspects} suspect`;
  return text;
};
const best = (cell) => (Array.isArray(cell) ? [...cell].sort((a, b) => RANK(a) - RANK(b))[0] : cell);

/** Rows (one per file) and columns (one per place) from getFileCopies' answer. */
export const buildGrid = (data) => {
  const places = data?.places || {};
  const cols = new Map();
  const rows = new Map();
  const add = (key, label, place, cell) => {
    if (!cols.has(place)) cols.set(place, places[place]?.label || place);
    if (!rows.has(key)) rows.set(key, { key, label, cells: {} });
    // Every copy is kept: one place may hold the file at several paths.
    (rows.get(key).cells[place] ??= []).push(cell);
  };
  for (const c of data?.copies || []) {
    add(`${c.sn || c.sharedVersionId}|${c.versionId}|${c.fileName}`, { sn: c.sn || c.sharedVersionId, fileName: c.fileName, versionId: c.versionId }, c.place, c);
  }
  for (const l of data?.legacy || []) {
    add(`old|${l.source}|${l.sn || l.subjectId}|${l.fileName}`, { sn: l.sn, fileName: l.fileName, versionId: null }, l.place, { state: "unverified", counts: false, old: true });
  }
  const order = (p) => (p.startsWith("cloud:") ? 2 : p.startsWith("nas:") ? 1 : p.startsWith("local:") ? 0 : 3);
  const columns = [...cols.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => order(a.id) - order(b.id) || a.label.localeCompare(b.label));
  return { columns, rows: [...rows.values()].sort((a, b) => String(a.label.sn).localeCompare(String(b.label.sn)) || a.label.fileName.localeCompare(b.label.fileName)) };
};

const fmtBytes = (n) => {
  if (typeof n !== "number") return "";
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  return `${Math.round(n / 1e3)} kB`;
};

export function TransfersGrid({ data }) {
  const { columns, rows } = buildGrid(data);
  if (!rows.length) return <div style={S.muted}>No copies recorded for this yet.</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>File</th>
            {columns.map((c) => <th key={c.id} style={S.th}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td style={S.td}>
                <div style={{ fontWeight: 500 }}>{r.label.fileName}</div>
                <div style={S.small}>{r.label.sn}{r.label.versionId ? ` · ${r.label.versionId}` : " · old record"}</div>
              </td>
              {columns.map((c) => {
                const list = r.cells[c.id] || [];
                const top = best(list);
                const anySuspect = list.some((x) => x.state === "suspect");
                return (
                  <td key={c.id} style={{ ...S.td, color: anySuspect ? "#B3261E" : top?.state === "verified" && top.counts ? C.green || "#2E7D32" : C.text }}
                    title={list.map((x) => [x.where, oneText(x), fmtBytes(x.bytes)].filter(Boolean).join(" · ")).join("\n")}>
                    {cellText(list)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OrdersList({ orders }) {
  if (!orders?.length) return null;
  return (
    <section style={{ marginTop: 22 }}>
      <div style={S.h2}>Orders</div>
      <table style={S.table}>
        <thead>
          <tr>{["Kind", "Machine", "File", "State", "Progress"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.orderId}>
              <td style={S.td}>{o.kind}</td>
              <td style={S.td}>{o.machine}</td>
              <td style={S.td}>{o.fileName || "—"}</td>
              <td style={S.td}>{o.state}{o.error ? ` (${o.error})` : ""}</td>
              <td style={S.td}>{o.progress && o.progress.bytesTotal ? `${Math.round((o.progress.bytesDone / o.progress.bytesTotal) * 100)}%` : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function TransfersView() {
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      const isSn = /^[a-z0-9]{6}\d{10}$/.test(q);
      setData(await bridge.getFileCopies(isSn ? { sn: q } : { jobId: q }));
    } catch (err) {
      setData(null);
      setError(err?.code === "functions/permission-denied" ? "Transfers are visible to admins only." : (err?.message || "Could not load"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={S.page}>
      <div style={S.h1}>Transfers</div>
      <div style={S.muted}>Where every file of a job is, and the orders moving or checking them. Read-only.</div>
      <form onSubmit={load} style={{ display: "flex", gap: 8, margin: "14px 0 18px" }}>
        <input aria-label="Job id or serial number" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Job id or serial number" style={S.input} />
        <button type="submit" style={S.button} disabled={loading}>{loading ? "Loading…" : "Show"}</button>
      </form>
      {error && <div style={{ ...S.muted, color: "#B3261E" }}>{error}</div>}
      {data && (data.complete?.copies === false || data.complete?.recentOrders === false || data.complete?.legacy === false) && (
        <div style={{ ...S.muted, color: "#8A5A00", marginBottom: 8 }}>
          Not everything is shown: this job has more copies, older records or ended orders than one page holds. Open orders are all shown.
        </div>
      )}
      {data && <TransfersGrid data={data} />}
      {data && <OrdersList orders={data.orders} />}
    </div>
  );
}

const S = {
  page: { padding: "24px 28px", fontFamily: font, color: C.text, fontSize: 13, maxWidth: 1200, margin: "0 auto", boxSizing: "border-box" },
  h1: { fontSize: 18, fontWeight: 500, marginBottom: 4 },
  h2: { fontSize: 15, fontWeight: 500, marginBottom: 8 },
  muted: { fontSize: 12.5, color: C.muted, fontWeight: 300 },
  small: { fontSize: 11, color: C.muted, fontWeight: 300 },
  input: { flex: "1 1 auto", maxWidth: 320, padding: "6px 10px", fontFamily: font, fontSize: 13, border: `1px solid ${C.border || "#ddd"}`, borderRadius: 6 },
  button: { padding: "6px 14px", fontFamily: font, fontSize: 13, borderRadius: 6, border: `1px solid ${C.border || "#ddd"}`, background: C.surfaceAlt || "#f5f5f5", cursor: "pointer" },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 12.5 },
  th: { textAlign: "left", fontWeight: 500, padding: "6px 10px", borderBottom: `1px solid ${C.border || "#ddd"}`, whiteSpace: "nowrap" },
  td: { padding: "6px 10px", borderBottom: `1px solid ${C.border || "#eee"}`, verticalAlign: "top" },
};
